import { datadogRum } from '@datadog/browser-rum';
import { Hydrate, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getCookie } from 'cookies-next';
import { NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement, ReactNode, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';

import Modal from '~components/Modal';
import Toast from '~components/Toast';
import { DEFAULT_DOCUMENT_TITLE, SERVER_TYPE } from '~constant/env';
import ButtonLogoutAdmin from '~features/admin/ButtonLogoutAdmin';
import * as ssid from '~features/admin/helper';
import Layout from '~features/layout';
import useMedia from '~hooks/useMedia';
import { queryClient } from '~lib/api/queryClient';
import GlobalStyle from '~lib/styles/GlobalStyle';
import theme from '~lib/styles/Theme';
import useEnviromentStore from '~store/enviroment';
import useModalStore from '~store/modal';
import { clearAbtestHistory } from '~utils/browser/clearAbtestHistory';
import { setAmplitudeUserProperties, setGtmDataLayer } from '~utils/common/thirdPartyEvent';
import { isClient } from '~utils/lodash/isServer';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

if (SERVER_TYPE === 'www') {
  datadogRum.init({
    applicationId: 'adc4a385-e29e-4af4-b2f9-ead005388b2a',
    clientToken: 'pub7d7fbd573aabd95e8087256e3fd63c58',
    site: 'datadoghq.com',
    service: 'kreditjob',
    env: SERVER_TYPE,
    sampleRate: 30,
    replaySampleRate: 0,
    trackInteractions: true,
  });
}

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();
  const { isOpen, modalClose, modalProps } = useModalStore();
  const { status } = useMedia();
  const { setIsApp } = useEnviromentStore();
  const getLayout = Component.getLayout || ((page) => <Layout>{page}</Layout>);

  useEffect(() => {
    if (isClient) {
      const isApp = Number(getCookie('is_app'));
      setIsApp(!!isApp);

      setAmplitudeUserProperties({
        application: isApp ? 'app' : 'web',
      });
    }
  }, []);

  useEffect(() => {
    const handleRouteChange = () => setGtmDataLayer('page__view');
    handleRouteChange();

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      <Head>
        <title key='title'>{DEFAULT_DOCUMENT_TITLE}</title>
        <meta
          name='viewport'
          content='width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no'
        />
        <meta key='og-url' property='og:url' content='https://kreditjob.com/' />
        <meta
          key='og-image'
          property='og:image'
          content='https://image.wanted.co.kr/kreditjob/www/sns-share-default.jpg'
        />
        <meta key='og-type' property='og:type' content='website' />
        <meta key='og-image-width' property='og:image:width' content='1200' />
        <meta key='og-image-height' property='og:image:height' content='630' />
        <link rel='shortcut icon' href='/favicon.ico' />
      </Head>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <ThemeProvider theme={theme}>
            <GlobalStyle />
            <Toast />
            {getLayout(<Component {...pageProps} />, status)}

            {isOpen && (
              <Modal
                isOpen={isOpen}
                handleClose={modalClose}
                {...modalProps}
                confirmButtonText={modalProps.confirmButtonText ?? '확인'}
                cancelButtonText={modalProps.cancelButtonText ?? '취소'}
              />
            )}
          </ThemeProvider>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </>
  );
}

export default MyApp;
